import { isSelectablePrivilege } from '@/features/users/roles-privilege/privilegeSelection';
import { Translation } from '@/hoslog-intl';
import { CheckSquareIcon } from '@/icons/check-square.icon';
import { cn } from '@/lib/utils';
import { MinusSquare, PlusSquare } from 'lucide-react';
import * as React from 'react';

export type FunctionItem = {
  id: string;
  permissionKey: string;
  displayName: string;
  httpMethod: string;
  routeTemplate: string;
  isAssigned: boolean;
};

export type MenuItem = {
  id: string;
  title: string;
  route: string;
  sortOrder: number;
  functions: FunctionItem[];
  children: MenuItem[];
};

type Props = {
  items: MenuItem[];
  level?: number;
  selectedFunctions: Set<string>;
  setSelectedFunctions: React.Dispatch<React.SetStateAction<Set<string>>>;
  parentId?: string;
  openItems?: Set<string>;
  setOpenItems?: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export function TreeAccordion({
  items,
  level = 0,
  selectedFunctions,
  setSelectedFunctions,
  openItems: externalOpenItems,
  setOpenItems: externalSetOpenItems,
}: Props) {
  const [internalOpenItems, setInternalOpenItems] = React.useState<Set<string>>(new Set());

  // Use external state if provided (for children), otherwise use internal
  const openItems = externalOpenItems || internalOpenItems;
  const setOpenItems = externalSetOpenItems || setInternalOpenItems;

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);

      // If it's a child item (level > 0), close all siblings first
      if (level > 0) {
        // Get all sibling IDs and remove them
        items.forEach((item) => {
          if (item.id !== id) {
            next.delete(item.id);
          }
        });
      }

      // Toggle the clicked item
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const toggleFunction = (functionId: string) => {
    setSelectedFunctions((prev) => {
      const next = new Set(prev);

      if (next.has(functionId)) {
        next.delete(functionId);
      } else {
        next.add(functionId);
      }

      return next;
    });
  };

  const selectAllFunctions = (functions: FunctionItem[]) => {
    setSelectedFunctions((prev) => {
      const next = new Set(prev);
      functions.filter(isSelectablePrivilege).forEach((fn) => next.add(fn.id));
      return next;
    });
  };

  const clearAllFunctions = (functions: FunctionItem[]) => {
    setSelectedFunctions((prev) => {
      const next = new Set(prev);
      functions.forEach((fn) => next.delete(fn.id));
      return next;
    });
  };

  return (
    <div className={level === 0 ? 'space-y-4' : ''}>
      {items.map((item, itemIdx) => {
        const isTop = level === 0;
        const hasChildren = item.children && item.children.length > 0;
        const hasFunctions = item.functions && item.functions.length > 0;
        const hasContent = hasChildren || hasFunctions;
        const isOpen = openItems.has(item.id);

        return (
          <div
            key={item.id}
            className={
              isTop
                ? 'border border-border-strong rounded-md overflow-hidden bg-card'
                : itemIdx > 0
                  ? 'border-t border-border-subtle'
                  : ''
            }
          >
            {/* Trigger Button */}
            <button
              type="button"
              onClick={() => toggleItem(item.id)}
              className={`
                w-full flex items-center gap-2 text-left
                ${isTop ? 'px-4 py-3 bg-surface-raised hover:bg-surface-sunken' : 'py-2.5 hover:bg-surface-raised relative'}
                transition-colors
              `}
              style={!isTop ? { paddingLeft: `${level * 24 + 16}px` } : {}}
            >
              {!isTop && (
                <>
                  {/* Vertical line */}
                  <div
                    className="absolute top-0 bottom-0 w-px bg-border-strong"
                    style={{ left: `${(level - 1) * 24 + 24}px` }}
                  />
                  {/* Horizontal connector */}
                  <div
                    className="absolute w-[17.4px] h-px bg-border-strong"
                    style={{
                      left: `${(level - 1) * 24 + 24}px`,
                      top: '50%',
                    }}
                  />
                </>
              )}
              {hasContent &&
                (isOpen ? (
                  <MinusSquare className="w-4 h-4 text-foreground-label flex-shrink-0" />
                ) : (
                  <PlusSquare className="w-4 h-4 text-foreground-label flex-shrink-0" />
                ))}
              {!hasContent && <PlusSquare className="w-4 h-4 text-foreground-label flex-shrink-0" />}
              <span className="text-sm font-medium text-foreground-heading">{item.title}</span>
            </button>

            {/* Content Area */}
            {isOpen && hasContent && (
              <div className={isTop ? 'bg-card' : ''}>
                {/* Render Functions if present */}
                {hasFunctions && (
                  <div
                    className="py-4 bg-card relative"
                    style={{ paddingLeft: `${level * 24 + 16}px`, paddingRight: '16px' }}
                  >
                    {!isTop && (
                      <div
                        className="absolute top-0 bottom-0 w-px bg-border-strong"
                        style={{ left: `${(level - 1) * 24 + 24}px` }}
                      />
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        type="button"
                        onClick={() => selectAllFunctions(item.functions)}
                        className="text-body font-medium text-destructive-text hover:underline cursor-pointer"
                      >
                        [<Translation id="userManagement.rolesAndPrivileges.button.selectAll" />]
                      </button>
                      <button
                        type="button"
                        onClick={() => clearAllFunctions(item.functions)}
                        className="text-body font-medium text-foreground-subtle hover:underline cursor-pointer"
                      >
                        [<Translation id="userManagement.rolesAndPrivileges.button.clearAll" />]
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 justify-start">
                      {item.functions.map((func) => {
                        return (
                          isSelectablePrivilege(func) && (
                            <label key={func.id} className="flex items-start gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedFunctions.has(func.id)}
                                onChange={() => toggleFunction(func.id)}
                                className="sr-only peer"
                              />
                              <CheckSquareIcon
                                className={cn(
                                  'w-4 h-4 mt-0.5 flex-shrink-0 transition-colors',
                                  selectedFunctions.has(func.id)
                                    ? 'text-destructive-text'
                                    : 'text-foreground-subtle group-hover:text-foreground-label',
                                )}
                              />
                              <span className="text-sm text-foreground-detail group-hover:text-foreground-heading">
                                {func.displayName}
                              </span>
                            </label>
                          )
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Render Children if present */}
                {hasChildren && (
                  <div className="border-t border-border-subtle">
                    <TreeAccordion
                      selectedFunctions={selectedFunctions}
                      setSelectedFunctions={setSelectedFunctions}
                      items={item.children}
                      level={level + 1}
                      parentId={item.id}
                      openItems={openItems}
                      setOpenItems={setOpenItems}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
